import {Injectable, NgZone, inject} from '@angular/core';
import {Observable} from 'rxjs';
import {DataSnapshot, onValue, ref} from 'firebase/database';
import * as _ from 'lodash';
import {AgentModel} from '../../../agents/angular/models/agent.model';
import {AgentsChanelService} from '../../../agents/angular/services/agents-chanel.service';
import {AgentStatusType} from '../../../agents/types/agentStatus.type';
import {injectLegacy} from '../../../commons/angular/utils/inject-legacy.util';
import type {FirebaseService} from '../../../persistence-layer/firebase.service';

export enum AgentClockEventType {
  ClockIn = 'ClockIn',
  ClockOut = 'ClockOut'
}

export interface AgentClockEvent {
  agentId: string;
  type: AgentClockEventType;
  /** The opened timecard on clock-in, the closed one on clock-out. */
  timeCardId: string | null;
}

/**
 * Clock-in and clock-out of agents. The server pushes only the agent on these events (no timecard channel),
 * so the timecards themselves are fetched from the API by the screen.
 */
@Injectable({providedIn: 'root'})
export class TimekeepingClockEventsService {

  private readonly ngZone = inject(NgZone);
  private readonly agentsChanelService = inject(AgentsChanelService);
  private readonly firebaseService = injectLegacy<FirebaseService>('firebaseService');

  /** Emits inside the Angular zone. Breaks and other in-day changes of an on-duty agent emit nothing. */
  watch(agentIds: string[]): Observable<AgentClockEvent> {
    return new Observable<AgentClockEvent>((subscriber) => {
      const {channel, database} = this.firebaseService;
      if (!channel || !database) {
        return undefined;
      }

      const unsubscribes = _.map(agentIds, (agentId) => {
        // undefined until the first push, which is the agent as it already is
        let previous: AgentModel | null | undefined;
        const agentRef = ref(database, `${channel.channelId}/${this.agentsChanelService.getChannelName()}/${agentId}`);
        return onValue(agentRef, (snapshot: DataSnapshot) => {
          const agent: AgentModel | null = snapshot.val();
          const event = previous === undefined ? null : toClockEvent(agentId, previous, agent);
          previous = agent;
          if (event) {
            // Firebase calls back outside the Angular zone (AngularJS opened the connection)
            this.ngZone.run(() => subscriber.next(event));
          }
        });
      });

      return () => _.forEach(unsubscribes, (unsubscribe) => unsubscribe());
    });
  }
}

function toClockEvent(agentId: string, previous: AgentModel | null, agent: AgentModel | null): AgentClockEvent | null {
  const previousTimeCardId = previous?.activeTimeCardId || null;
  const activeTimeCardId = agent?.activeTimeCardId || null;

  // Clock-in always opens a new timecard; ending a break keeps the same one
  if (activeTimeCardId && activeTimeCardId !== previousTimeCardId) {
    return {agentId, type: AgentClockEventType.ClockIn, timeCardId: activeTimeCardId};
  }
  if (agent?.status === AgentStatusType.OffDuty && previous?.status !== AgentStatusType.OffDuty) {
    return {
      agentId,
      type: AgentClockEventType.ClockOut,
      timeCardId: agent.lastActiveTimeCardReference?.id || previousTimeCardId
    };
  }
  return null;
}
